import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Plus, Trash2, Edit, Phone, Mail, MapPin, Star, Search, RefreshCw,
  Wrench, Landmark, Handshake, Users as UsersIcon, ShieldCheck, Ruler, Home, Briefcase,
  KeyRound, Eye, EyeOff, UserCheck, UserX, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmButton } from '../components/ConfirmButton';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

// ============ Credentials Dialog (set/reset artisan login) ============
const CredentialsDialog = ({ open, onOpenChange, contact, onChanged }) => {
  const [email, setEmail] = useState(contact?.email || '');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setEmail(contact?.email || ''); setPassword(''); } }, [open, contact]);

  const hasAccount = contact?.has_account;

  const save = async () => {
    if (!email) { toast.error('Email requis'); return; }
    if (!hasAccount && !password) { toast.error('Mot de passe requis à la création'); return; }
    if (password && password.length < 6) { toast.error('Mot de passe trop court (min 6)'); return; }
    setSaving(true);
    try {
      await axios.post(
        `${API}/artisans/${contact.id}/credentials`,
        { email, password: password || null },
        { withCredentials: true },
      );
      toast.success(hasAccount ? 'Compte mis à jour' : 'Compte créé — identifiants à transmettre à l\'artisan');
      onChanged?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const removeAccess = async () => {
    setSaving(true);
    try {
      await axios.delete(`${API}/artisans/${contact.id}/credentials`, { withCredentials: true });
      toast.success('Accès artisan supprimé');
      onChanged?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-emerald-400" />
            {hasAccount ? 'Modifier l\'accès' : 'Créer un accès'} — {contact?.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-zinc-400 text-xs">Email (identifiant de connexion)</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="artisan@exemple.fr"
              className="bg-zinc-950 border-zinc-800 text-white"
              data-testid="credentials-email"
            />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs">
              Mot de passe {hasAccount ? '(laisser vide pour ne pas changer)' : '(requis à la création)'}
            </Label>
            <div className="relative">
              <Input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={hasAccount ? '••••••' : 'Minimum 6 caractères'}
                className="bg-zinc-950 border-zinc-800 text-white pr-10"
                data-testid="credentials-password"
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                tabIndex={-1}
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <p className="text-[11px] text-zinc-500 leading-relaxed bg-zinc-950 border border-zinc-800 rounded p-2">
            💡 Transmettez ces identifiants à l'artisan par mail ou SMS. Il pourra se connecter sur cette URL et accédera uniquement à ses Bons de commande.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 justify-end">
          {hasAccount && (
            <ConfirmButton
              title="Supprimer l'accès ?"
              description="L'artisan ne pourra plus se connecter, mais son ancien compte reste conservé dans la base."
              confirmLabel="Supprimer l'accès"
              onConfirm={removeAccess}
              trigger={
                <Button variant="outline" className="border-red-600/50 text-red-400 hover:bg-red-500/10" disabled={saving} data-testid="credentials-remove">
                  <UserX className="w-4 h-4 mr-1" /> Supprimer l'accès
                </Button>
              }
            />
          )}
          <Button variant="outline" className="border-zinc-700 text-zinc-300" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-500 text-white" onClick={save} disabled={saving} data-testid="credentials-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <UserCheck className="w-4 h-4 mr-1" />}
            {hasAccount ? 'Mettre à jour' : 'Créer le compte'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};


const CATEGORIES = [
  { value: 'artisan',       label: 'Artisans',       icon: Wrench,       metiers: ['plombier','electricien','couvreur','macon','peintre','menuisier','chauffagiste','carreleur','plaquiste','terrassier','architecte','autre'] },
  { value: 'notaire',       label: 'Notaires',       icon: Landmark,     metiers: ['etude_notariale','notaire_associe','clerc','autre'] },
  { value: 'courtier',      label: 'Courtiers',      icon: Handshake,    metiers: ['credit_immobilier','assurance','autre'] },
  { value: 'banque',        label: 'Banques',        icon: Landmark,     metiers: ['conseiller','directeur_agence','pro','autre'] },
  { value: 'diagnostiqueur',label: 'Diagnostiqueurs',icon: ShieldCheck,  metiers: ['dpe','amiante','plomb','electricite','gaz','termites','erp','autre'] },
  { value: 'geometre',      label: 'Géomètres',      icon: Ruler,        metiers: ['geometre_expert','topographe','autre'] },
  { value: 'agent_immo',    label: 'Agents immo',    icon: Home,         metiers: ['agent','mandataire','negociateur','directeur_agence','autre'] },
  { value: 'autre',         label: 'Autres',         icon: Briefcase,    metiers: ['expert_comptable','avocat','assureur','autre'] },
];

const CATEGORY_MAP = Object.fromEntries(CATEGORIES.map(c => [c.value, c]));

const StarRating = ({ value, onChange, readOnly }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map(i => (
      <button key={i} type="button" disabled={readOnly} onClick={() => onChange?.(i)}
        className={`${readOnly ? '' : 'hover:scale-110'} transition-transform`}>
        <Star className={`w-4 h-4 ${i <= (value || 0) ? 'fill-amber-400 text-amber-400' : 'text-zinc-600'}`} />
      </button>
    ))}
  </div>
);

const ContactForm = ({ contact, defaultCategory, onSave, onClose }) => {
  const initial = {
    name: contact?.name || '',
    category: contact?.category || defaultCategory || 'artisan',
    metier: contact?.metier || '',
    siret: contact?.siret || '',
    telephone: contact?.telephone || '',
    email: contact?.email || '',
    adresse: contact?.adresse || '',
    ville: contact?.ville || '',
    tarif_horaire: contact?.tarif_horaire || '',
    notes: contact?.notes || '',
    note_qualite: contact?.note_qualite || 0,
  };
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const catCfg = CATEGORY_MAP[form.category] || CATEGORY_MAP.artisan;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = { ...form, tarif_horaire: form.tarif_horaire ? parseFloat(form.tarif_horaire) : null };
      if (contact?.id) {
        await axios.put(`${API}/artisans/${contact.id}`, data, { withCredentials: true });
        toast.success('Contact mis à jour');
      } else {
        await axios.post(`${API}/artisans`, data, { withCredentials: true });
        toast.success('Contact ajouté');
      }
      onSave(); onClose();
    } catch { toast.error('Erreur'); } finally { setLoading(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <Label className="text-zinc-300">Nom / Entreprise</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="bg-zinc-950 border-zinc-800 text-white" data-testid="contact-name-input" />
        </div>
        <div>
          <Label className="text-zinc-300">Catégorie</Label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v, metier: '' })}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white"><SelectValue /></SelectTrigger>
            <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-zinc-300">Spécialité</Label>
          <Select value={form.metier || catCfg.metiers[0]} onValueChange={(v) => setForm({ ...form, metier: v })}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white"><SelectValue /></SelectTrigger>
            <SelectContent>{catCfg.metiers.map(m => <SelectItem key={m} value={m}>{m.replace(/_/g,' ')}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-zinc-300">Téléphone</Label>
          <Input value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} className="bg-zinc-950 border-zinc-800 text-white" placeholder="06 XX XX XX XX" />
        </div>
        <div>
          <Label className="text-zinc-300">Email</Label>
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-zinc-950 border-zinc-800 text-white" />
        </div>
        <div>
          <Label className="text-zinc-300">Ville</Label>
          <Input value={form.ville} onChange={(e) => setForm({ ...form, ville: e.target.value })} className="bg-zinc-950 border-zinc-800 text-white" />
        </div>
        <div>
          <Label className="text-zinc-300">Tarif horaire (EUR)</Label>
          <Input type="number" value={form.tarif_horaire} onChange={(e) => setForm({ ...form, tarif_horaire: e.target.value })} className="bg-zinc-950 border-zinc-800 text-white" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-zinc-300">SIRET</Label>
          <Input
            value={form.siret}
            onChange={(e) => setForm({ ...form, siret: e.target.value.replace(/\s/g, '') })}
            maxLength={14}
            className="bg-zinc-950 border-zinc-800 text-white font-mono"
            placeholder="14 chiffres"
            data-testid="contact-siret-input"
          />
          <p className="text-[10px] text-zinc-500 mt-1">Utile pour vérifier l'entreprise sur Pappers / Infogreffe et figurer sur les BDC.</p>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-zinc-300">Note qualité</Label>
          <div className="pt-2">
            <StarRating value={form.note_qualite} onChange={(v) => setForm({ ...form, note_qualite: v })} />
          </div>
        </div>
      </div>
      <div>
        <Label className="text-zinc-300">Notes</Label>
        <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-zinc-950 border-zinc-800 text-white min-h-[60px]" placeholder="Spécialités, disponibilité..." />
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onClose} className="border-zinc-700 text-zinc-300">Annuler</Button>
        <Button type="submit" disabled={loading} className="text-white"
          style={{ backgroundColor: 'var(--accent-color, #10B981)' }}
          data-testid="save-contact-button">{contact?.id ? 'Modifier' : 'Ajouter'}</Button>
      </div>
    </form>
  );
};

export const NetworkPage = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [credentialsDialog, setCredentialsDialog] = useState({ open: false, contact: null });

  const fetchContacts = async () => {
    try {
      const res = await axios.get(`${API}/artisans`, { withCredentials: true });
      setContacts(res.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { fetchContacts(); }, []);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/artisans/${id}`, { withCredentials: true });
      toast.success('Contact supprimé');
      fetchContacts();
    } catch {
      toast.error('Erreur');
    }
  };

  const counts = useMemo(() => {
    const c = { all: contacts.length };
    contacts.forEach(ct => { const k = ct.category || 'artisan'; c[k] = (c[k] || 0) + 1; });
    return c;
  }, [contacts]);

  const filtered = contacts.filter(a => {
    const cat = a.category || 'artisan';
    const okCat = activeCategory === 'all' || cat === activeCategory;
    const okSearch = !search
      || a.name?.toLowerCase().includes(search.toLowerCase())
      || a.ville?.toLowerCase().includes(search.toLowerCase())
      || a.metier?.toLowerCase().includes(search.toLowerCase());
    return okCat && okSearch;
  });

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="network-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white font-['Outfit'] tracking-tight">Réseau</h1>
          <p className="text-zinc-400 mt-1">Tous vos contacts pros pour vos projets</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setLoading(true); fetchContacts(); }} className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 text-sm transition-colors" data-testid="refresh-button">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Actualiser
          </button>
          <Button className="bg-white text-black hover:bg-zinc-200" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="add-contact-button">
            <Plus className="w-4 h-4 mr-2" /> Ajouter un contact
          </Button>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white">{editing ? 'Modifier le contact' : 'Nouveau contact'}</DialogTitle></DialogHeader>
          <ContactForm
            contact={editing}
            defaultCategory={activeCategory !== 'all' ? activeCategory : 'artisan'}
            onSave={fetchContacts}
            onClose={() => { setDialogOpen(false); setEditing(null); }}
          />
        </DialogContent>
      </Dialog>

      {/* Category tabs */}
      <div className="flex flex-wrap gap-2" data-testid="category-tabs">
        <button
          onClick={() => setActiveCategory('all')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
            activeCategory === 'all' ? 'bg-zinc-800 text-white' : 'border-zinc-800 text-zinc-400 hover:text-white'
          }`}
          style={activeCategory === 'all' ? { borderColor: 'var(--accent-color, #10B981)' } : undefined}
          data-testid="cat-tab-all"
        >
          <UsersIcon className="w-4 h-4" />
          <span className="text-sm font-medium">Tous</span>
          <span className="text-xs text-zinc-500">({counts.all || 0})</span>
        </button>
        {CATEGORIES.map(c => (
          <button
            key={c.value}
            onClick={() => setActiveCategory(c.value)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
              activeCategory === c.value ? 'bg-zinc-800 text-white' : 'border-zinc-800 text-zinc-400 hover:text-white'
            }`}
            style={activeCategory === c.value ? { borderColor: 'var(--accent-color, #10B981)' } : undefined}
            data-testid={`cat-tab-${c.value}`}
          >
            <c.icon className="w-4 h-4" />
            <span className="text-sm font-medium">{c.label}</span>
            <span className="text-xs text-zinc-500">({counts[c.value] || 0})</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input placeholder="Rechercher par nom, ville ou spécialité..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-zinc-950 border-zinc-800 text-white" />
          </div>
        </CardContent>
      </Card>

      {filtered.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(c => {
            const cat = CATEGORY_MAP[c.category] || CATEGORY_MAP.artisan;
            return (
              <Card key={c.id} className="bg-zinc-900 border-zinc-800 card-hover">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="min-w-0">
                      <h3 className="font-semibold text-white truncate">{c.name}</h3>
                      <p className="text-sm flex items-center gap-1.5" style={{ color: 'var(--accent-color, #10B981)' }}>
                        <cat.icon className="w-3 h-3" />
                        <span>{cat.label}</span>
                        {c.metier && <span className="text-zinc-500">· {c.metier.replace(/_/g,' ')}</span>}
                      </p>
                    </div>
                    <StarRating value={c.note_qualite} readOnly />
                  </div>
                  <div className="space-y-1 text-sm text-zinc-400 mt-3">
                    {c.telephone && <div className="flex items-center gap-2"><Phone className="w-3 h-3" /> <a href={`tel:${c.telephone}`} className="hover:text-white">{c.telephone}</a></div>}
                    {c.email && <div className="flex items-center gap-2"><Mail className="w-3 h-3" /> <a href={`mailto:${c.email}`} className="hover:text-white truncate">{c.email}</a></div>}
                    {c.ville && <div className="flex items-center gap-2"><MapPin className="w-3 h-3" /> {c.ville}</div>}
                    {c.siret && <div className="text-[11px] font-mono text-zinc-500">SIRET : {c.siret}</div>}
                    {c.tarif_horaire && <div className="font-mono text-amber-400">{c.tarif_horaire} EUR/h</div>}
                  </div>
                  {c.notes && <p className="text-xs text-zinc-500 mt-2 line-clamp-2">{c.notes}</p>}
                  {c.category === 'artisan' && (
                    <div className={`mt-3 p-2 rounded border text-xs flex items-center justify-between gap-2 ${c.has_account ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-zinc-950 border-zinc-800'}`} data-testid={`artisan-account-${c.id}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        {c.has_account ? <UserCheck className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> : <KeyRound className="w-3.5 h-3.5 text-zinc-500 shrink-0" />}
                        <span className={`truncate ${c.has_account ? 'text-emerald-400' : 'text-zinc-500'}`}>
                          {c.has_account ? 'Accès actif' : 'Aucun accès'}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant={c.has_account ? 'ghost' : 'outline'}
                        className={c.has_account ? 'h-6 text-[11px] text-emerald-300 hover:bg-emerald-500/10 px-2' : 'h-6 text-[11px] border-emerald-600/50 text-emerald-400 hover:bg-emerald-500/10 px-2'}
                        onClick={() => setCredentialsDialog({ open: true, contact: c })}
                        data-testid={`set-credentials-${c.id}`}
                      >
                        {c.has_account ? 'Modifier' : 'Créer'}
                      </Button>
                    </div>
                  )}
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" variant="outline" className="border-zinc-700 text-zinc-300" onClick={() => { setEditing(c); setDialogOpen(true); }}>
                      <Edit className="w-3 h-3 mr-1" /> Modifier
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleDelete(c.id)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="py-16 text-center">
            <UsersIcon className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">Aucun contact</h3>
            <p className="text-zinc-500">Ajoutez vos premiers contacts pros</p>
          </CardContent>
        </Card>
      )}

      {credentialsDialog.contact && (
        <CredentialsDialog
          open={credentialsDialog.open}
          onOpenChange={(v) => setCredentialsDialog((prev) => ({ ...prev, open: v }))}
          contact={credentialsDialog.contact}
          onChanged={fetchContacts}
        />
      )}
    </div>
  );
};
